import React, { useState, useEffect, useCallback } from "react";
import { Modal, Tag, Spin, Alert } from "antd";
import axios from "axios";
import moment from "moment";
import { fmtFechaDia, claveFechaDia } from "../../../../Components/Items";
import { etiquetaCausaFiniquito } from "../causaFiniquito";

// Mismo molde que los historiales de INFONAVIT / FONACOT / Movimientos Permanentes
// (PreNomina/InfonvitEmpleado/InfonvitEmpleado.js): Modal sin footer y tabla HTML cruda. NO se usa
// TableItem porque hardcodea `pagination` y `scroll` y los ignora si se los pasas.
// La paleta es la del "Historial de Sueldos" de esta misma pantalla, para que se vean hermanos.
const TH = {
  padding: "6px 10px", fontSize: 11, fontWeight: 600, textAlign: "center",
  border: "1px solid #4a6a7a", whiteSpace: "nowrap", color: "#fff", background: "#5a7a8a",
};
const TD = {
  border: "1px solid #e0e0e0", padding: "6px 10px", textAlign: "center",
  fontSize: 12, verticalAlign: "middle",
};

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

// La duración se calcula sobre las CLAVES de día, nunca sobre los Date crudos: las fechas del
// empleado y las de un finiquito pueden estar guardadas con horas distintas (ver
// Components/fmtFechaDia.js) y restarlas directo mete el offset en la cuenta.
const duracion = (inicio, fin) => {
  const cIni = claveFechaDia(inicio);
  if (!cIni) return "—";
  const a = moment(cIni, "YYYY-MM-DD");
  const cFin = claveFechaDia(fin);
  const b = cFin ? moment(cFin, "YYYY-MM-DD") : moment().startOf("day");
  if (b.isBefore(a)) return "—";

  const anios = b.diff(a, "years");
  const meses = b.diff(a.clone().add(anios, "years"), "months");
  if (anios === 0 && meses === 0) return `${b.diff(a, "days") + 1} días`;
  const partes = [];
  if (anios > 0) partes.push(`${anios} ${anios === 1 ? "año" : "años"}`);
  if (meses > 0) partes.push(`${meses} ${meses === 1 ? "mes" : "meses"}`);
  return partes.join(" ");
};

// Por qué se ignoró un reingreso que SÍ está capturado. Lo comparten este modal y el aviso del
// encabezado de Editar Empleado; las llaves son las de `reingresoDescartado` que devuelve
// explicarInicioRelacionLaboral (erp_backend/Utils/empleado.utils.js).
export const MOTIVO_DESCARTE = {
  no_posterior_al_alta: "no es posterior a la fecha de alta",
  centinela_1899: "es la fecha cero de Excel (30/12/1899) que dejó el archivo externo",
  invalida: "no es una fecha válida",
};

/**
 * Historial de la relación laboral: los periodos cerrados (uno por cada finiquito aplicado, que
 * congeló su propia fecha de alta y de baja, más las bajas sin finiquito que se cerraron con un
 * Reingreso —`Empleado.reingresos`—) y el vigente. De sólo lectura.
 *
 * Se carga al ABRIR el modal, no al montar la pantalla: `datosParaEmpleado` ya trae ~15 catálogos.
 */
const ModalHistorialRelacionLaboral = ({
  visible, onCancel, empleadoId, empresaId, headersToken, nombreEmpleado,
}) => {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [periodos, setPeriodos] = useState([]);
  const [relacionLaboral, setRelacionLaboral] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await axios.post(
        "empleados/historialRelacionLaboral",
        { empresaId, empleadoId },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        setPeriodos(res.data.periodos || []);
        setRelacionLaboral(res.data.relacionLaboral || null);
      } else {
        setError(res.data.message || "No se pudo consultar el historial");
      }
    } catch (e) {
      setError("No se pudo consultar el historial");
    } finally {
      setCargando(false);
    }
  }, [empresaId, empleadoId, headersToken]);

  useEffect(() => {
    if (visible && empleadoId) cargar();
  }, [visible, empleadoId, cargar]);

  const vigente = periodos.find((p) => p.vigente);

  return (
    <Modal
      title={`Historial de relación laboral${nombreEmpleado ? ` — ${nombreEmpleado}` : ""}`}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={720}
    >
      <Spin spinning={cargando}>
        {error && (
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
        )}

        {/* La fecha que manda, arriba y en grande: es el dato que se venía a buscar. */}
        {relacionLaboral && (
          <div
            style={{
              background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 4,
              padding: "10px 12px", marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>
              Inicio de relación laboral vigente
            </div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {relacionLaboral.inicio ? fmtFechaDia(relacionLaboral.inicio) : "Sin capturar"}{" "}
              {relacionLaboral.origen === "reingreso" && (
                <Tag color="gold" style={{ marginLeft: 4 }}>manda el reingreso</Tag>
              )}
              {relacionLaboral.origen === "alta" && (
                <Tag color="blue" style={{ marginLeft: 4 }}>fecha de alta</Tag>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
              {relacionLaboral.inicio
                ? "Es la fecha que usan el CFDI de nómina, el recibo, el aguinaldo y el finiquito."
                : "Sin fecha de alta ni de reingreso, el timbrado usaría la fecha de inicio del "
                  + "periodo. Captura la fecha de alta en la ficha del empleado."}
            </div>
            {relacionLaboral.reingresoDescartado && (
              <div style={{ fontSize: 11, color: "#a8071a", marginTop: 6 }}>
                Hay una fecha de reingreso capturada que <b>se ignora</b> porque{" "}
                {MOTIVO_DESCARTE[relacionLaboral.reingresoDescartado] || "no es utilizable"}.
              </div>
            )}
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 620 }}>
            <thead>
              <tr>
                <th style={TH}>Inicio</th>
                <th style={TH}>Fin</th>
                <th style={TH}>Duración</th>
                <th style={TH}>Causa de la baja</th>
                <th style={TH}>Finiquito</th>
              </tr>
            </thead>
            <tbody>
              {!cargando && periodos.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...TD, textAlign: "center", color: "#aaa", padding: 24 }}>
                    Este empleado no tiene fecha de alta capturada, así que no hay historial que
                    mostrar.
                  </td>
                </tr>
              )}
              {periodos.map((p, i) => (
                <tr
                  key={p.finiquitoId || `vigente-${i}`}
                  style={{ background: p.vigente ? "#fffbe6" : (i % 2 === 0 ? undefined : "#fafafa") }}
                >
                  <td style={{ ...TD, fontWeight: p.vigente ? 700 : 400 }}>
                    {fmtFechaDia(p.inicio)}
                    {p.vigente && <Tag color="gold" style={{ marginLeft: 6 }}>Vigente</Tag>}
                  </td>
                  <td style={TD}>
                    {p.abierto
                      ? <span style={{ color: "#2d7d46", fontWeight: 600 }}>En curso</span>
                      : fmtFechaDia(p.fin)}
                    {/* Lo escribe el flujo de Reingreso: la relación que cerró este periodo
                        terminó y el empleado volvió en esa fecha. */}
                    {p.fechaReingresoPosterior && (
                      <div style={{ fontSize: 11, color: "#2d7d46" }}>
                        Reingresó el {fmtFechaDia(p.fechaReingresoPosterior)}
                      </div>
                    )}
                  </td>
                  <td style={TD}>{duracion(p.inicio, p.fin)}</td>
                  <td style={{ ...TD, textAlign: "left" }}>
                    {p.abierto ? "—" : etiquetaCausaFiniquito(p.causa)}
                    {p.motivo && (
                      <div style={{ fontSize: 11, color: "#888" }}>{p.motivo}</div>
                    )}
                  </td>
                  <td style={TD}>
                    {p.finiquitoId ? (
                      <>
                        <Tag color={p.estatusFiniquito === "timbrado" ? "green" : "blue"}>
                          {p.estatusFiniquito === "timbrado" ? "Timbrado" : "Aplicado"}
                        </Tag>
                        <div style={{ fontSize: 11, fontFamily: "monospace", color: "#555" }}>
                          {fmtMXN(p.totalNeto)}
                        </div>
                      </>
                    ) : p.abierto ? "—" : (
                      <span style={{ fontSize: 11, color: "#999" }}>Baja sin finiquito</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {vigente && !vigente.abierto && (
          <div style={{ fontSize: 11, color: "#888", marginTop: 10 }}>
            El empleado está dado de baja: no hay una relación laboral abierta.
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ModalHistorialRelacionLaboral;
